import { px } from '@/utils/hubScale'
import React from 'react'
import { Popover } from 'antd'
import styled from 'styled-components'

import { stepperStepsFromProject } from '@features/projects/hub/components/projectStepLabel'
import type { ProjectStepType } from '@autoriz/shared/project'

const COLORS = {
  ink100: '#f1f1ef',
  ink300: '#c8c7c0',
  ink400: '#8a8980',
  ink500: '#65645e',
  ink700: '#353430',
  ink900: '#14130f',
  greenPrimary: '#00b96b',
  green200: '#bbf7d0',
  green700: '#166534',
}

// No outer margin: the stepper is mounted inside BreadcrumbHeader's meta panel,
// which owns the spacing around it.
const Root = styled.div`
  display: flex;
  gap: ${px(14)};
  font-family: 'Geist', 'Inter', system-ui, sans-serif;
`

const StepCell = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: ${px(6)};
  min-width: 0;
`

const Bar = styled.div<{ $status: 'done' | 'current' | 'todo' }>`
  height: ${px(4)};
  border-radius: ${px(2)};
  background: ${(p) =>
    p.$status === 'done'
      ? COLORS.green700
      : p.$status === 'current'
        ? COLORS.greenPrimary
        : COLORS.ink100};
  transition: background 150ms ease;
`

const Label = styled.div<{ $status: 'done' | 'current' | 'todo' }>`
  display: flex;
  align-items: center;
  gap: ${px(6)};
  font-size: ${px(12)};
  letter-spacing: 0.01em;
  font-weight: ${(p) => (p.$status === 'current' ? 600 : 500)};
  color: ${(p) =>
    p.$status === 'current'
      ? COLORS.ink900
      : p.$status === 'done'
        ? COLORS.ink700
        : COLORS.ink400};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Num = styled.span<{ $status: 'done' | 'current' | 'todo' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${px(18)};
  height: ${px(18)};
  padding: 0 ${px(5)};
  border-radius: ${px(4)};
  background: ${(p) =>
    p.$status === 'current' || p.$status === 'done'
      ? COLORS.green200
      : COLORS.ink100};
  color: ${(p) =>
    p.$status === 'current' || p.$status === 'done'
      ? COLORS.green700
      : COLORS.ink500};
  font-size: ${px(10.5)};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`

const PopoverText = styled.div`
  max-width: ${px(240)};
  font-size: ${px(12.5)};
  line-height: 1.5;
  color: ${COLORS.ink700};
`

export type HubLifecycleStepperProps = {
  currentStep: ProjectStepType
  /**
   * `projects.deposit_mode`. Only the "Envoi" phase's explanation depends on it
   * — "puis déposés en mairie" describes the commercial's errand, which is not
   * what happens when the architect files the dossier online (#638).
   */
  depositMode?: string | null
}

/**
 * Salesman project lifecycle stepper — the 7-position horizontal bar
 * matching the v2 mockup's `rq-slim-stepper`. Each cell shows a
 * coloured bar above a small numbered label, and hovering a cell
 * reveals a popover with a one-line explanation of that phase.
 *
 * Status partition reuses `stepperStepsFromProject` from
 * `projectStepLabel.ts` (introduced in #321), which collapses the
 * 13-value `ProjectStep` enum onto 7 lifecycle positions.
 */
export const HubLifecycleStepper: React.FC<HubLifecycleStepperProps> = ({
  currentStep,
  depositMode,
}) => {
  const steps = stepperStepsFromProject({ step: currentStep, depositMode })
  return (
    <Root
      data-cy="salesman-hub-lifecycle-stepper"
      role="progressbar"
      aria-label="Étapes du projet"
    >
      {steps.map((s, idx) => (
        <Popover
          key={s.num}
          title={`${idx + 1}. ${s.title}`}
          content={<PopoverText>{s.explanation}</PopoverText>}
          mouseEnterDelay={0.2}
        >
          <StepCell
            data-cy={`salesman-hub-lifecycle-step--${idx + 1}`}
            data-status={s.status}
            tabIndex={0}
          >
            <Bar $status={s.status} />
            <Label $status={s.status}>
              <Num $status={s.status}>{idx + 1}</Num>
              <span>{s.title}</span>
            </Label>
          </StepCell>
        </Popover>
      ))}
    </Root>
  )
}
